import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { GetPhoneCallParticipantInfoService } from "@modules/api/get-phone-call-participant-info.service.generated";
import { UpdatePhoneCallService } from "@modules/api/update-phone-call.service.generated";
import { UserSession } from "@modules/api/user-session.generated";
import { CurrentEnvironmentService } from "@modules/common/services/current-environment.service";
import { UserSessionService } from "@modules/common/services/user-session.service";
import { ParticipantPhoneCallIsVerifiedDialogComponent } from "@modules/participant-shared/components/participant-phone-call-is-verified-dialog/participant-phone-call-is-verified-dialog.component";
import { PhoneCallParticipantInfo } from "@modules/participant-shared/models/phone-call-participant-info";
import { SetCurrentPhoneCallArgs } from "@modules/participant-shared/models/set-current-phone-call-args";
import { MostRecentParticipantIdService } from "@modules/participant-shared/services/most-recent-participant-id.service";
import { BehaviorSubject, Observable, skip, Subscription } from "rxjs";

/**
 * Service to keep track of information related to the current phone call.
 * The user can be considered to be in a phone call if they arrive at Stingray from Five9.
 * Information is stored in sessionStorage to keep it scoped to one browser tab and
 * persist across page refreshes.
 */
@Injectable({
    providedIn: 'root',
})
export class CurrentPhoneCallService {
    /**
     * Key for sessionStorage data.
     */
    private readonly _sessionStorageKey: string = 'CurrentPhoneCallService';

    /**
     * Subject that emits the phone call info.
     */
    private readonly _phoneCallParticipantInfoSubject = new BehaviorSubject<PhoneCallParticipantInfo | undefined>(undefined);

    private _subscriptions: Subscription[] = [];

    constructor(
        private readonly _updatePhoneCallService: UpdatePhoneCallService,
        private readonly _getPhoneCallParticipantInfoService: GetPhoneCallParticipantInfoService,
        private readonly _mostRecentParticipantIdService: MostRecentParticipantIdService,
        private readonly _dialog: MatDialog,
        private readonly _userSessionService: UserSessionService,
        private readonly _currentEnvironmentService: CurrentEnvironmentService,
    ) {
        //Handle initialization that only needs to happen when the page is refreshed during a phone call.
        //This would otherwise be handled in setCurrentPhoneCall.
        if (this.isCurrentlyInPhoneCall()) {
            //Create subscriptions to handle changes as needed.
            this._subscriptions = this.createSubscriptions();

            //If there is phone call info in session storage, initialize the subject with it.
            if (this.phoneCallParticipantInfo) {
                this.emitParticipantInfo(this.phoneCallParticipantInfo);
            }
        }
    }

    /**
     * Returns whether the user is currently in a phone call.
     */
    public isCurrentlyInPhoneCall(): boolean {
        return !!this.callHistoryId;
    }

    /**
     * Returns an observable that emits information for the current phone call, or undefined if
     * there is no current phone call. Emits once upon subscription and once every time the
     * current phone call information changes. The current phone call information changes when
     * the phone call starts, when the user navigates to a page for a different participant
     * while on a phone call, and when the phone call ends (in this context, "ends" just means
     * that Stingray is no longer able to track the phone call, not necessarily that either
     * party has hung up the actual phone call).
     */
    public currentPhoneCallChanged(): Observable<PhoneCallParticipantInfo | undefined> {
        return this._phoneCallParticipantInfoSubject.asObservable();
    }

    /**
     * Sets the current phone call using the given information. This method should only be called once,
     * when the site first becomes aware that the user is in a phone call.
     */
    public setCurrentPhoneCall(args: SetCurrentPhoneCallArgs): void {
        if (this.isCurrentlyInPhoneCall()) {
            return; //This shouldn't happen, but let's prevent it anyway
        }

        this.callHistoryId = args.callHistoryId;
        this.participantId = args.participantId;
        if (args.piiMatches) {
            this.verifiedParticipantId = args.participantId;
        }

        //Create subscriptions to handle changes as needed.
        this._subscriptions = this.createSubscriptions();
        
        //Initialize the participant info if possible. Otherwise, just emit empty participant info.
        if (this.participantId) {
            this.updateParticipantInfoAndShowVerificationDialog(this.participantId);
        } else {
            this.setAndEmitParticipantInfo({
                participantName: undefined,
                employeeId: undefined,
                dateOfBirth: undefined,
                ssnLastFour: undefined,
                address: undefined,
            });
        }
    }

    /**
     * Returns true if the current phone call participant is verified, false if they are
     * not verified or if there is no current phone call participant.
     * A verified participant is one that correctly provided their PII when calling in.
     */
    public isParticipantVerified(): boolean {
        return !!this.participantId && !!this.verifiedParticipantId && this.participantId == this.verifiedParticipantId;
    }

    private createSubscriptions(): Subscription[] {
        return [
            this.createParticipantIdChangedSubscription(),
            this.createUserSessionChangedSubscription(),
            this.createEnvironmentChangedSubscription(),
        ];
    }

    private createParticipantIdChangedSubscription(): Subscription {
        return this._mostRecentParticipantIdService.participantIdChanged()
            .subscribe(participantId => this.handleParticipantIdChanged(participantId));
    }

    private handleParticipantIdChanged(participantId: number): void {
        //Check that the new participant is actually different from the phone call participant id.
        //Note that storing the phone call participant id separately is only necessary because
        //this service may become aware of the participant id BEFORE it gets put in the URL, and
        //we don't want to update the phone call info twice on the first navigation.
        if (this.participantId == participantId) {
            return;
        }

        //The participant id is being changed. Associate this phone call with the new participant,
        //emit the new participant info, and show the verification dialog.

        this.participantId = participantId;

        this._updatePhoneCallService.updatePhoneCall({
            callHistoryId: this.callHistoryId!,
            participantId: this.participantId,
        });

        this.updateParticipantInfoAndShowVerificationDialog(this.participantId);
    }

    private updateParticipantInfoAndShowVerificationDialog(participantId: number): void {
        //Fetch the participant info from the database.
        this._getPhoneCallParticipantInfoService.getPhoneCallParticipantInfo({
            participantId: participantId,
        }).subscribe(result => {
            //Emit the new participant info.
            this.setAndEmitParticipantInfo({
                participantName: result.participantName,
                employeeId: result.employeeId,
                dateOfBirth: result.dateOfBirth,
                ssnLastFour: result.ssnLastFour,
                address: result.address,
            });

            //Show the dialog that shows the user whether the new participant is verified.
            this._dialog.open<ParticipantPhoneCallIsVerifiedDialogComponent>(ParticipantPhoneCallIsVerifiedDialogComponent);
        });
    }

    private setAndEmitParticipantInfo(phoneCallParticipantInfo: PhoneCallParticipantInfo | undefined): void {
        this.phoneCallParticipantInfo = phoneCallParticipantInfo;
        this.emitParticipantInfo(phoneCallParticipantInfo);
    }

    private emitParticipantInfo(phoneCallParticipantInfo: PhoneCallParticipantInfo | undefined): void {
        this._phoneCallParticipantInfoSubject.next(phoneCallParticipantInfo);
    }

    private createUserSessionChangedSubscription(): Subscription {
        return this._userSessionService.userSessionChanged()
            .subscribe(userSession => this.handleUserSessionChanged(userSession));
    }

    private handleUserSessionChanged(userSession: UserSession | undefined): void {
        //End the phone call if the user logs out.
        if (!userSession) {
            this.endCurrentPhoneCall();
        }
    }

    private createEnvironmentChangedSubscription(): Subscription {
        return this._currentEnvironmentService.environmentChanged
            .pipe(skip(1)) //environmentChanged emits on subscription, ignore that
            .subscribe(() => this.handleEnvironmentChanged());
    }

    private handleEnvironmentChanged(): void {
        //The current phone call info is only valid for the current environment,
        //so end the phone call if the environment changes.
        this.endCurrentPhoneCall();
    }

    public endCurrentPhoneCall(): void {
        if (!this.isCurrentlyInPhoneCall()) {
            return;
        }
        
        //Remove all session storage data and emit the end of the phone call.
        this.removeSessionStorageData();
        this.emitParticipantInfo(this.phoneCallParticipantInfo);

        //Clean up subscriptions.
        for (let subscription of this._subscriptions) {
            subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    /**
     * Session storage for call history id.
     * See {@link CurrentPhoneCallServiceSessionStorageData.callHistoryId}.
     */
    private get callHistoryId(): number | undefined {
        return this.getSessionStorageData()?.callHistoryId;
    }

    private set callHistoryId(callHistoryId: number | undefined) {
        this.updateSessionStorageData(sessionStorageData => sessionStorageData.callHistoryId = callHistoryId);
    }

    /**
     * Session storage for participant id.
     * See {@link CurrentPhoneCallServiceSessionStorageData.participantId}.
     */
    private get participantId(): number | undefined {
        return this.getSessionStorageData()?.participantId;
    }

    private set participantId(participantId: number | undefined) {
        this.updateSessionStorageData(sessionStorageData => sessionStorageData.participantId = participantId);
    }

    /**
     * Session storage for verified participant id.
     * See {@link CurrentPhoneCallServiceSessionStorageData.verifiedParticipantId}.
     */
    private get verifiedParticipantId(): number | undefined {
        return this.getSessionStorageData()?.verifiedParticipantId;
    }

    private set verifiedParticipantId(verifiedParticipantId: number | undefined) {
        this.updateSessionStorageData(sessionStorageData => sessionStorageData.verifiedParticipantId = verifiedParticipantId);
    }

    /**
     * Session storage for participant info.
     * See {@link CurrentPhoneCallServiceSessionStorageData.phoneCallParticipantInfo}.
     */
    private get phoneCallParticipantInfo(): PhoneCallParticipantInfo | undefined {
        return this.getSessionStorageData()?.phoneCallParticipantInfo;
    }

    private set phoneCallParticipantInfo(phoneCallParticipantInfo: PhoneCallParticipantInfo | undefined) {
        this.updateSessionStorageData(sessionStorageData => sessionStorageData.phoneCallParticipantInfo = phoneCallParticipantInfo);
    }

    private getSessionStorageData(): CurrentPhoneCallServiceSessionStorageData | undefined {
        const serializedSessionStorageData = sessionStorage.getItem(this._sessionStorageKey);
        return serializedSessionStorageData ? JSON.parse(serializedSessionStorageData) : undefined;
    }

    private updateSessionStorageData(update: (sessionStorageData: CurrentPhoneCallServiceSessionStorageData) => void): void {
        //Get the session storage data, or create it if it doesn't exist yet.
        let sessionStorageData = this.getSessionStorageData();
        if (!sessionStorageData) {
            sessionStorageData = {
                callHistoryId: undefined,
                participantId: undefined,
                verifiedParticipantId: undefined,
                phoneCallParticipantInfo: undefined,
            };
        }

        //Apply the given update to the session storage data, then save it in session storage.
        update(sessionStorageData);
        sessionStorage.setItem(this._sessionStorageKey, JSON.stringify(sessionStorageData));
    }

    private removeSessionStorageData(): void {
        sessionStorage.removeItem(this._sessionStorageKey);
    }
}

interface CurrentPhoneCallServiceSessionStorageData {
    /**
     * The id of the current phone call, or undefined if there is no current phone call.
     */
    callHistoryId: number | undefined,
    
    /**
     * The id of the participant of the current phone call, or undefined if we don't yet
     * know who the participant is, or if there is no current phone call.
     */
    participantId: number | undefined,

    /**
     * The id of the verified participant, or undefined if no participant was verified.
     */
    verifiedParticipantId: number | undefined,

    /**
     * Info for the participant of the current phone call, or undefined if the info has not
     * yet been fetched, or if there is no current phone call.
     */
    phoneCallParticipantInfo: PhoneCallParticipantInfo | undefined,
}